import { getAllNomineesByEdition, type NomineeDetailResponse } from '@api/nominees-api';
import Breadcrumbs from '@components/breadcrumbs';
import { useEdition } from '@hooks/use-edition';
import { useQuery } from '@tanstack/react-query';
import { getAvatarColor, getInitials } from '@utils/avatar-utils';
import { Award, MapPin, Medal, Trophy } from 'lucide-react';
import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';

interface CategoryWinners {
  readonly categoryId: string;
  readonly categoryName: string;
  readonly winners: readonly NomineeDetailResponse[];
}

const RANK_LABEL: Record<number, string> = { 1: '1st', 2: '2nd', 3: '3rd' };

function fullName(nominee: NomineeDetailResponse): string {
  return [nominee.participant.name, nominee.participant.surname].filter(Boolean).join(' ');
}

function groupWinnersByCategory(nominees: readonly NomineeDetailResponse[]): CategoryWinners[] {
  const byCategory = new Map<string, CategoryWinners>();
  for (const nominee of nominees) {
    if (!nominee.isWinner) continue;
    const existing = byCategory.get(nominee.category.id);
    if (existing) {
      (existing.winners as NomineeDetailResponse[]).push(nominee);
    } else {
      byCategory.set(nominee.category.id, {
        categoryId: nominee.category.id,
        categoryName: nominee.category.name,
        winners: [nominee],
      });
    }
  }
  const groups = [...byCategory.values()];
  for (const group of groups) {
    (group.winners as NomineeDetailResponse[]).sort(
      (a, b) => (a.ranking ?? 99) - (b.ranking ?? 99),
    );
  }
  groups.sort((a, b) => a.categoryName.localeCompare(b.categoryName));
  return groups;
}

function WinnerCard({ nominee }: { readonly nominee: NomineeDetailResponse }): React.JSX.Element {
  const rank = nominee.ranking ?? 0;
  const name = fullName(nominee);
  return (
    <div className={`ehof-card ehof-card--rank-${rank}`}>
      <div className="ehof-card__medal">
        <Medal size={rank === 1 ? 22 : 18} />
        <span>{RANK_LABEL[rank] ?? `#${rank}`}</span>
      </div>
      <div className="ehof-card__avatar" style={{ background: getAvatarColor(name) }}>
        {getInitials(name)}
      </div>
      <h3 className="ehof-card__name">{name}</h3>
      {nominee.participant.city && (
        <p className="ehof-card__city">
          <MapPin size={12} />
          {nominee.participant.city}
        </p>
      )}
    </div>
  );
}

function CategorySection({ group }: { readonly group: CategoryWinners }): React.JSX.Element {
  return (
    <section className="ehof-category">
      <h2 className="ehof-category__title">{group.categoryName}</h2>
      <div className="ehof-podium">
        {group.winners.map((nominee) => (
          <WinnerCard key={nominee.id} nominee={nominee} />
        ))}
      </div>
    </section>
  );
}

export default function EditionHallOfFame(): React.JSX.Element {
  const { editionId } = useParams<{ editionId: string }>();
  const { edition } = useEdition(editionId);

  const { data: nominees, isLoading } = useQuery({
    queryKey: ['edition-hall-of-fame', editionId],
    queryFn: () => getAllNomineesByEdition(editionId!),
    enabled: !!editionId,
  });

  const groups = useMemo(() => groupWinnersByCategory(nominees ?? []), [nominees]);
  const editionName = edition?.name ?? 'Edition';

  return (
    <>
      <Breadcrumbs
        items={[
          { label: 'Editions', to: '/editions' },
          { label: editionName },
          { label: 'Hall of Fame' },
        ]}
      />

      <div className="ehof-hero">
        <span className="ehof-hero__badge">
          <Trophy size={14} />
          {edition ? `${edition.country} · ${edition.year}` : 'Hall of Fame'}
        </span>
        <h1 className="ehof-hero__title">Hall of Fame</h1>
        <p className="ehof-hero__subtitle">{editionName}</p>
      </div>

      {isLoading ? (
        <div className="ehof-state" role="status">
          Loading winners…
        </div>
      ) : groups.length === 0 ? (
        <div className="ehof-state ehof-state--empty" role="status">
          <Award size={40} className="ehof-state__icon" />
          <h3>No winners yet</h3>
          <p>Finalize this edition by importing winners to populate the Hall of Fame.</p>
          <Link to="/editions" className="btn btn-secondary">
            Back to editions
          </Link>
        </div>
      ) : (
        <div className="ehof-grid">
          {groups.map((group) => (
            <CategorySection key={group.categoryId} group={group} />
          ))}
        </div>
      )}
    </>
  );
}
